import { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { ProfileContext } from '../../../App';
import { GET_ROLES } from '../../../GraphQL/roles/queries';
import { GET_CANDIDATES } from '../../../GraphQL/candidates/queries';
import { GET_JOB_MATCHES, GET_CANDIDATE_MATCHES } from '../../../GraphQL/matches/queries';
import { GET_ROLE_TOPICS, GET_CANDIDATE_TOPICS } from '../../../GraphQL/messages/queries';
import { DashboardData } from '../utils/dashboardTypes';
import { processInboxItems } from '../utils/inboxProcessor';

export function useDashboardData() {
  const { userProfile, connections } = useContext(ProfileContext);

  // Fetch roles (limit 50 to respect backend max page size)
  const { data: rolesData, loading: rolesLoading, error: rolesError } = useQuery(GET_ROLES, {
    variables: { skip: 0, take: 50, order: [{ updatedAt: 'DESC' }] },
    fetchPolicy: 'network-only', // Always fetch fresh data for dashboard
  });

  // Fetch candidates (limit 50 to respect backend max page size)
  const { data: candidatesData, loading: candidatesLoading, error: candidatesError } = useQuery(
    GET_CANDIDATES,
    {
      variables: { skip: 0, take: 50, order: { updatedAt: 'DESC' } },
      fetchPolicy: 'network-only',
    }
  );

  // Fetch job matches (limit 50 to respect backend max page size)
  const { data: jobMatchesData, loading: jobMatchesLoading, error: jobMatchesError } = useQuery(
    GET_JOB_MATCHES,
    {
      variables: { skip: 0, take: 50, order: [{ updatedAt: 'DESC' }] },
      fetchPolicy: 'network-only',
    }
  );

  // Fetch candidate matches (limit 50 to respect backend max page size)
  const { data: candidateMatchesData, loading: candidateMatchesLoading, error: candidateMatchesError } = useQuery(
    GET_CANDIDATE_MATCHES,
    {
      variables: { skip: 0, take: 50, order: [{ updatedAt: 'DESC' }] },
      fetchPolicy: 'network-only',
    }
  );

  // Fetch job topics - messages (limit 50 to respect backend max page size)
  const { data: jobTopicsData, loading: jobTopicsLoading, error: jobTopicsError } = useQuery(
    GET_ROLE_TOPICS,
    {
      variables: { skip: 0, take: 50, order: [{ updatedAt: 'DESC' }] },
      fetchPolicy: 'network-only',
    }
  );

  // Fetch candidate topics - messages (limit 50 to respect backend max page size)
  const { data: candidateTopicsData, loading: candidateTopicsLoading, error: candidateTopicsError } = useQuery(
    GET_CANDIDATE_TOPICS,
    {
      variables: { skip: 0, take: 50, order: [{ updatedAt: 'DESC' }] },
      fetchPolicy: 'network-only',
    }
  );

  // Aggregate loading states (show loading if critical queries are loading)
  const loading = rolesLoading || candidatesLoading || jobMatchesLoading;

  // Aggregate errors
  const error = rolesError || candidatesError || jobMatchesError ||
                jobMatchesError || candidateMatchesError || jobTopicsError || candidateTopicsError;

  // Construct dashboard data
  const data: DashboardData = {
    roles: rolesData?.jobs?.items || [],
    candidates: candidatesData?.candidates?.items || [],
    jobMatches: jobMatchesData?.jobMatches?.items || [],
    candidateMatches: candidateMatchesData?.candidateMatches?.items || [],
    jobTopics: jobTopicsData?.jobTopics?.items || [],
    candidateTopics: candidateTopicsData?.candidateTopics?.items || [],
    userProfile: userProfile || null,
    connections: connections?.items || [],
  };

  // Process inbox items from topics and matches
  const inboxItems = useMemo(() => {
    if (loading) return [];
    return processInboxItems(data);
  }, [data, loading]);

  return {
    data,
    inboxItems,
    loading,
    error,
  };
}
